import { Col, Form, Input, Row, Space } from 'antd';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import Select from 'react-select';
import Modal from '../../../main/components/Modal';
import DropzoneWithState from '../../components/DropzoneWithState';
import { useFetch } from '../../../main/utils/makeRequest';
import * as Route from '../../support/serverRoutes';
import ModalDeleteConfirmation from '../../components/ModalDeleteConfirmation';
import { CellFullName } from '../../components/UserList';
import SelectUser from '../Selects/SelectUser';
import DateRangeComponent from './DateRangeComponent';
import { Divider } from '../../components/Divider';
import { SELECT_STYLES } from '../../../main/utils/const';
import { TenantLists } from '../Lists/TenantLists';
import Button from '../../../main/components/Button';
import ModalAnonymiseUsers from '../../components/ModalAnonymiseUsers';
import { hasAccessAnonymiseUser } from '../../utils/access';

const ModalUpdateEstablishment = ({
  visible,
  establishmentId,
  locales,
  onClose,
}) => {
  if (!establishmentId) return <></>;
  const [initialValues, setInitialValues] = useState(null);
  const [
    visibilityModalDeleteConfirmation,
    setVisibilityModalDeleteConfirmation,
  ] = useState(false);
  const [adminOptions, setAdminOptions] = useState([]);
  const [anonymiseVisible, setAnonymiseVisible] = useState(false);
  const [anonymiseLoading, setAnonymiseLoading] = useState(false);
  const [tenantOptions, setTenantOptions] = useState([]);

  const {
    user,
    admin: {
      adminOptions: { collection: adminList, loading: adminLoading },
      admins: { collection: admins },
    },
    establishment: { data: establishmentData, uploading },
    tenants: { data: tenants },
  } = useSelector((state) => state);

  const {
    establishment: { updateEstablishment, updateEstablishmentLogo },
    establishments: { deactivateEstablishment, reload, anonymiseUsers },
    admin: { fetchAdmins, fetchEstablishmentAdmins, addAdmin, removeAdmin },
    tenants: { fetchTenantsByEstablishmentId, addTenant, removeTenant },
  } = useDispatch();

  const [authenticationMode, setAuthenticationMode] = useState(null);

  const [data, loading] = useFetch({
    url: Route.establishment(establishmentId),
  });

  const [form] = Form.useForm();

  const handleOpenModalDeleteConfirmation = (e) => {
    e.preventDefault();
    setVisibilityModalDeleteConfirmation(true);
  };

  const handleCloseModalDeleteConfirmation = () =>
    setVisibilityModalDeleteConfirmation(false);

  useEffect(() => {
    fetchAdmins({ establishmentId });
    fetchEstablishmentAdmins({ establishmentId });
    fetchTenantsByEstablishmentId({ establishmentId });
  }, []);

  useEffect(() => {
    if (adminList) {
      setAdminOptions(adminList);
    }
  }, [adminList]);

  useEffect(() => {
    if (tenants) {
      setTenantOptions(tenants);
    }
  }, [tenants]);

  const handleSelect = (userId) => {
    addAdmin({ establishmentId, userId });
  };

  const handleDelete = (userId) => {
    removeAdmin({ establishmentId, userId });
  };

  const handleLogoChange = (file) => {
    updateEstablishmentLogo({
      id: establishmentId,
      logo: file,
    });
  };

  const handleTenantAdd = (name, tenantUuid) => {
    addTenant({ name, tenantUuid });
  };

  const handleTenantRemove = (tenantUuid) => {
    removeTenant({ tenantUuid });
  };

  const handleFinish = (values) => {
    const valuesWithAuthenticationMode = {
      ...values,
      authenticationMode: values.authenticationMode.value,
    };
    updateEstablishment({
      id: establishmentId,
      values: valuesWithAuthenticationMode,
      adminIds: admins.map((a) => a.id),
      tenants: tenants.map((t) => {
        return { name: t.name, uuid: t.uuid };
      }),
      after: [reload, onClose],
    });
  };

  const handleAnonymiseUsers = (confirmationPassword, groups) => {
    setAnonymiseLoading(true);
    anonymiseUsers({
      establishmentId,
      confirmationPassword,
      groups,
      afterSuccess: () => {
        setAnonymiseVisible(false);
        reload();
        onClose();
      },
      finish: () => setAnonymiseLoading(false),
    });
  };

  const handleDeactivateEstablishment = () => {
    deactivateEstablishment({ id: establishmentId });
    handleCloseModalDeleteConfirmation();
    onClose();
  };

  useEffect(() => {
    if (data) {
      setInitialValues({
        ...data,
        licenceLength: data.licenceLength ? Number(data.licenceLength) : null,
        licenceStartDate: data.licenceStartDate
          ? moment(data.licenceStartDate).format('YYYY-MM-DD')
          : null,
        expireAt: data.expireAt
          ? moment(data.expireAt).format('YYYY-MM-DD')
          : null,
        authenticationLabels: data.authenticationLabels,
        authenticationMode: {
          value: data.authenticationMode,
          label: data.authenticationLabels.find(
            (label) => label.value === data.authenticationMode,
          )?.label,
        },
      });
      setAuthenticationMode(data.authenticationMode);
    }
  }, [data]);

  const handleAuthenticationModeChange = (args) => {
    setAuthenticationMode(args.value);
  };

  const showTenantOptions = () => {
    return (
      authenticationMode === 'sso_on' || authenticationMode === 'sso_forced'
    );
  };

  console.log('Locales: ', locales);
  console.log('locales.admin: ', locales.admin);
  console.log('locales.admin.logo: ', locales.admin.logo);

  return (
    <>
      {establishmentId && initialValues && !loading && (
        <Modal
          className="md"
          isOpen={
            visible && establishmentId && initialValues && !loading && visible
          }
          onClose={onClose}
          title={locales.admin.updateEstablishment}
        >
          <Form
            layout="vertical"
            onFinish={handleFinish}
            initialValues={initialValues}
            form={form}
          >
            <div className="mt-20">
              <Row gutter={40}>
                <Col style={{ flex: '1' }}>
                  <Form.Item
                    label={locales.admin.name}
                    className="admin-input-label"
                    name="name"
                  >
                    <Input
                      placeholder={locales.admin.addNameOfEstablishment}
                      className="admin-input"
                    />
                  </Form.Item>
                  <Form.Item
                    label={locales.admin.subdomain}
                    className="admin-input-label"
                    name="subdomain"
                  >
                    <Input
                      placeholder={locales.admin.addEstablishmentsSubdomain}
                      className="admin-input"
                    />
                  </Form.Item>
                  <Row gutter={10}>
                    <DateRangeComponent
                      form={form}
                      isUpdate
                      initialValues={initialValues}
                      locales={locales}
                    />
                  </Row>

                  <SelectUser
                    avatarVisible
                    users={adminOptions}
                    loading={uploading || adminLoading}
                    columns={[
                      {
                        dataIndex: 'email',
                        render: (row) => {
                          return <CellFullName user={row} />;
                        },
                      },
                    ]}
                    labelOfSelect={
                      locales.admin.inviteAdminsToJoinYourEstablishment
                    }
                    placeholderOfSelect={locales.admin.chooseAdminByEmail}
                    labelOfList={locales.admin.admins}
                    selectedIds={admins.map((admin) => admin.id)}
                    onSelect={handleSelect}
                    onDelete={handleDelete}
                  />
                </Col>
                <Col style={{ flex: '1' }}>
                  <Form.Item
                    label={locales.admin.logoText}
                    className="admin-input-label"
                    name="file"
                  >
                    <div className="dropzone-size">
                      <DropzoneWithState
                        onChange={handleLogoChange}
                        locales={locales}
                        initUrl={
                          establishmentData
                            ? establishmentData?.logo?.url
                            : initialValues?.logo?.url
                        }
                      />
                    </div>
                  </Form.Item>
                  <Form.Item
                    label={locales.admin.totalLicence}
                    className="admin-input-label"
                    name="maxLicensesCount"
                  >
                    <Input
                      type="number"
                      min={1}
                      placeholder={locales.admin.days}
                      className="admin-input"
                    />
                  </Form.Item>
                  <Form.Item
                    label={locales.admin.licenceLength}
                    className="admin-input-label"
                    name="licenceLength"
                  >
                    <Input
                      type="number"
                      min={1}
                      placeholder={locales.admin.days}
                      className="admin-input"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
            <div className="mt-20 mb-20">
              <Divider />
            </div>
            <Row>
              <Form.Item
                label={locales.admin.authenticationMode}
                className="admin-input-label"
                name="authenticationMode"
                style={{ width: '300px' }}
              >
                <Select
                  id="authenticationModes"
                  options={initialValues?.authenticationLabels}
                  className="bmc-form-select"
                  styles={SELECT_STYLES}
                  onChange={handleAuthenticationModeChange}
                />
              </Form.Item>
            </Row>
            {showTenantOptions() && (
              <Row>
                <TenantLists
                  tenants={tenantOptions}
                  addTenant={handleTenantAdd}
                  removeTenant={handleTenantRemove}
                />
                {tenantOptions.length === 0 && (
                  <Form.Item
                    label={locales.admin.noTenantsAdded}
                    className="admin-input-label"
                  />
                )}
              </Row>
            )}
            <div className="mt-20">
              <Divider />
            </div>
            <div className="flex space-between mt-20">
              <Space>
                <Button
                  disabled={data?.studentsCount > 0}
                  type="text"
                  onClick={handleOpenModalDeleteConfirmation}
                  danger
                  variant="destructiveGhost"
                >
                  {locales.common.deleteText}
                </Button>
                {hasAccessAnonymiseUser(user) && (
                  <Button
                    type="text"
                    onClick={(e) => {
                      e.preventDefault();
                      setAnonymiseVisible(true);
                    }}
                    danger
                    variant="destructiveGhost"
                  >
                    {locales.admin.anonymiseAllUsers}
                  </Button>
                )}
              </Space>
              <Space>
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={uploading}
                >
                  {locales.common.cancel}
                </Button>
                <Button type="primary" htmlType="submit" loading={uploading}>
                  {locales.common.save}
                </Button>
              </Space>
            </div>
          </Form>
          {/* Inside the establishment modal so headlessui keeps focus in the nested dialog */}
          {hasAccessAnonymiseUser(user) && (
            <ModalAnonymiseUsers
              visible={anonymiseVisible}
              loading={anonymiseLoading}
              title={locales.admin.anonymiseEstablishmentUsersTitle}
              groups={[
                { key: 'students', label: locales.admin.users },
                { key: 'educators', label: locales.admin.educators },
                { key: 'admins', label: locales.admin.admins },
              ]}
              onClose={() => setAnonymiseVisible(false)}
              onConfirm={handleAnonymiseUsers}
            />
          )}
        </Modal>
      )}
      <ModalDeleteConfirmation
        visible={visibilityModalDeleteConfirmation}
        confirmationTitle={locales.admin.areYouSureToDeleteThisEstablishment}
        onClose={handleCloseModalDeleteConfirmation}
        onDelete={handleDeactivateEstablishment}
      />
    </>
  );
};

ModalUpdateEstablishment.propTypes = {
  onClose: PropTypes.func.isRequired,
  establishmentId: PropTypes.number,
};
export default ModalUpdateEstablishment;
